import React, { useState, useRef, useEffect } from "react";
import { colors } from "../../constants/theme";

export type StatusOption = "Publish" | "Processing" | "Archived";

interface StatusDropdownProps {
  currentStatus?: string;
  onStatusChange: (status: StatusOption) => void;
  disabled?: boolean;
}

const STATUS_OPTIONS: StatusOption[] = ["Publish", "Processing", "Archived"];

// Map frontend status to backend status
export function mapStatusToBackend(status: StatusOption): string {
  const mapping: Record<StatusOption, string> = {
    "Publish": "completed",
    "Processing": "processing",
    "Archived": "archived",
  };
  return mapping[status];
}

// Map backend status to frontend status
export function mapStatusFromBackend(status: string | undefined): StatusOption {
  if (!status) return "Publish";
  const normalized = status.toLowerCase();
  if (normalized === "completed") return "Publish";
  if (normalized === "processing") return "Processing";
  if (normalized === "archived") return "Archived";
  return "Publish"; // Default
}

// Get color for status
function getStatusColor(status: StatusOption): { background: string; border: string; color: string } {
  switch (status) {
    case "Publish":
      return {
        background: "#2e7d32", // Dark green
        border: "#1b5e20",
        color: "#fff",
      };
    case "Processing":
      return {
        background: "#f9a825", // Yellow/Orange
        border: "#f57f17",
        color: "#fff",
      };
    case "Archived":
      return {
        background: "#c62828", // Red
        border: "#b71c1c",
        color: "#fff",
      };
    default:
      return {
        background: colors.card,
        border: colors.border,
        color: colors.text,
      };
  }
}

export const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  onStatusChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const displayStatus = mapStatusFromBackend(currentStatus);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (status: StatusOption) => {
    onStatusChange(status);
    setIsOpen(false);
  };

  const statusColors = getStatusColor(displayStatus);

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <div style={{ display: "flex", gap: 0 }}>
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            padding: "8px 16px",
            borderRadius: "8px 0 0 8px",
            border: `1px solid ${statusColors.border}`,
            background: statusColors.background,
            color: statusColors.color,
            fontWeight: 600,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            borderRight: "none",
          }}
        >
          {displayStatus}
        </button>
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            padding: "8px 8px",
            borderRadius: "0 8px 8px 0",
            border: `1px solid ${statusColors.border}`,
            borderLeft: "none",
            background: statusColors.background,
            color: statusColors.color,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ▼
        </button>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            right: 0,
            marginBottom: 4,
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            boxShadow: colors.shadow,
            zIndex: 1000,
            minWidth: 150,
            overflow: "hidden",
          }}
        >
          {STATUS_OPTIONS.map((status) => {
            const itemColors = getStatusColor(status);
            const isSelected = status === displayStatus;
            
            return (
              <button
                key={status}
                onClick={() => handleSelect(status)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  border: "none",
                  background: isSelected ? itemColors.background : "transparent",
                  color: isSelected ? itemColors.color : itemColors.background,
                  textAlign: "left",
                  cursor: "pointer",
                  fontWeight: isSelected ? 600 : 500,
                  borderLeft: `3px solid ${itemColors.background}`,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = `${itemColors.background}20`; // 20% opacity
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                {status}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

